import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import { FHButton, FBoxContainer, FProgressBar, FHSignatureDialog } from '../../components/base';
import {
  clientActions,
  clientSupervisionActions,
  providerActions,
  providerAssignmentActions,
  treatmentPlanActions,
} from '../../store';
import useClientSupervisionEditable from '../../hooks/useClientSupervisionEditable';
import { BAYS, MINIMUM_REQUIRED_SENTENCE_LENGTH } from '../../constants';
import { history } from '../../helpers';
import { getUtcOffset } from '../../utils/common';
import PageContainer from '../../components/layout/PageContainer';
import CurrentClinicalStatus from '../../components/layout/supervision-notes/client/CurrentClinicalStatus';
import OverallProgressTowardMastery from '../../components/layout/supervision-notes/client/OverallProgressTowardMastery';
import ResponseToTreatment from '../../components/layout/supervision-notes/client/ResponseToTreatment';
import TherapySession from '../../components/layout/supervision-notes/client/TherapySession';
import ClientSupervisionInfo from '../../components/layout/supervision-notes/client/ClientSupervisionInfo';
import { resolveClientIdFromFormOrSupervision } from '../../components/layout/supervision-notes/client/utils/general';
import { getBayActiveAtDateTime } from '../../components/layout/supervision-notes/utils/general';
import { useClientCohortAssignments } from '../../components/layout/supervision-notes/hooks';
import SessionNarrative from '../../components/layout/supervision-notes/client/SessionNarrative';
import DefineTargetPhaseChangeLine from '../../components/layout/supervision-notes/client/define-target-phase-change-line';
import { FORTA_BLACK } from '../../utils/colors';

import type { RootState } from '../../store';
import type { CheckedTherapyFields, IClientSupervision, IProvider } from '../../utils/types';
import type { NarrativeUpdateContext } from '../../components/layout/supervision-notes/types';

function ClientSupervisionNote() {
  const { id: supervisionId } = useParams();
  const openNoteIdRef = useRef(supervisionId);
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useClientSupervisionEditable(false);
  const { current: clientSupervision, loading: clientSupervisionLoading } = useSelector(
    (state: RootState) => state.clientSupervisions,
  );
  const [openSignature, setOpenSignature] = useState<boolean>(false);
  const [signing, setSigning] = useState<boolean>(false);
  const [updatedSupervision, setUpdatedSupervision] = useState<IClientSupervision | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [checkedTherapyFields, setCheckedTherapyFields] = useState<Array<CheckedTherapyFields>>([]);
  const [showNarrativeError, setShowNarrativeError] = useState<boolean>(false);

  useEffect(() => {
    setShowNarrativeError(false);
  }, [updatedSupervision?.id]);

  const { user } = useSelector((state: RootState) => state.auth);

  const isEditableForCohortAssignments = useClientSupervisionEditable() || !updatedSupervision?.id;

  const resolvedClientIdForCohort = useMemo(
    () => resolveClientIdFromFormOrSupervision('', updatedSupervision?.client_id),
    [updatedSupervision?.client_id],
  );

  const { clientCohortAssignments, cohortAssignmentsLoading } = useClientCohortAssignments({
    clientId: resolvedClientIdForCohort,
    isEditable: isEditableForCohortAssignments,
    getAccessTokenSilently,
    userRoles: user?.roles,
  });

  const bayActiveAtDateTime = useMemo(
    () =>
      getBayActiveAtDateTime(
        clientCohortAssignments,
        updatedSupervision?.date_start ? moment(updatedSupervision.date_start) : null,
      ),
    [clientCohortAssignments, updatedSupervision?.date_start],
  );

  const isBay6 = useMemo(
    () => bayActiveAtDateTime?.toUpperCase() === BAYS.BAY_6,
    [bayActiveAtDateTime],
  );

  useEffect(() => {
    return () => {
      dispatch(clientActions.clearSelectedClient());
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      dispatch(providerAssignmentActions.clearAssignment());
      dispatch(providerActions.clearCoAssignedProviders());
    };
  }, []);

  useEffect(() => {
    if (!supervisionId || supervisionId === 'new') {
      dispatch(clientActions.clearSelectedClient());
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      dispatch(clientSupervisionActions.clearState());
      dispatch(providerAssignmentActions.clearAssignment());
      dispatch(providerActions.clearCoAssignedProviders());
      return;
    }

    dispatch(providerActions.clearCoAssignedProviders());

    const fetchSupervisionNote = async () => {
      const { error } = await dispatch(
        clientSupervisionActions.getSupervision({ supervisionId, getAccessTokenSilently }),
      );

      if (error) {
        history.navigate('/dashboard');
      }
    };

    fetchSupervisionNote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisionId]);

  useEffect(() => {
    setUpdatedSupervision(clientSupervision);
  }, [clientSupervision]);

  useEffect(() => {
    openNoteIdRef.current = supervisionId;
  }, [supervisionId]);

  const handleUpdate = async (
    supervision: IClientSupervision,
    narrativeContext?: NarrativeUpdateContext,
  ) => {
    if (!supervision) return false;
    setUpdatedSupervision(supervision);
    if (!supervision.id) return false;
    if (!isBay6 && !supervision.bt_participants?.primary) return false;

    const primaryId = supervision.bt_participants?.primary?.id;
    const btParticipantsPayload = isBay6
      ? { primary: null, secondary: [] }
      : {
          primary: primaryId != null ? Number(primaryId) : null,
          secondary:
            supervision.bt_participants?.secondary?.map((provider: IProvider) => provider.id) ?? [],
        };

    setSaving(true);
    const res = await dispatch(
      clientSupervisionActions.updateSupervision({
        supervisionId: supervision.id,
        data: {
          ...supervision,
          duration: Number(supervision.duration),
          utc_offset: getUtcOffset(moment(new Date())),
          bt_participants: btParticipantsPayload,
        },
        narrativeContext,
        getAccessTokenSilently,
      }),
    );

    const openNoteId = openNoteIdRef.current;
    if (
      openNoteId == null ||
      openNoteId === 'new' ||
      String(supervision.id) !== openNoteId ||
      (narrativeContext != null && String(narrativeContext.noteId) !== openNoteId)
    ) {
      setSaving(false);
      return false;
    }

    if (!res || res.error) {
      if (clientSupervision?.bt_participants?.primary || isBay6) {
        setUpdatedSupervision(clientSupervision);
      } else {
        setUpdatedSupervision(
          clientSupervision
            ? {
                ...clientSupervision,
                bt_participants: {
                  primary: supervision.bt_participants?.primary ?? null,
                  secondary: [],
                },
              }
            : supervision,
        );
      }

      if (res?.error?.code === '409' && supervision.id) {
        // The note changed on the server since this update was built. Refetch it so the
        // stale cached narrative suggestion is replaced instead of restored only locally.
        await dispatch(
          clientSupervisionActions.getSupervision({
            supervisionId: supervision.id,
            getAccessTokenSilently,
          }),
        );
        setSaving(false);
        return 'conflict';
      }

      setSaving(false);
      return false;
    }

    setSaving(false);
    return true;
  };

  const validate = () => {
    setShowNarrativeError(false);
    if (!updatedSupervision) return false;

    const {
      clinical_status,
      response_to_treatment,
      session_narrative,
      overall_progress_toward_mastery,
      bt_participants,
    } = updatedSupervision;

    if (!bt_participants?.primary && !isBay6) {
      toast.error('Primary Behavioral Technician must be selected');
      return false;
    }

    if (checkedTherapyFields?.length === 0) {
      toast.error('At least one checkbox must be selected');
      return false;
    }

    if (checkedTherapyFields.some((field) => !updatedSupervision[field])) {
      toast.error('Selected Checkbox input fields should have a value');
      return false;
    }

    if (!clinical_status) {
      toast.error('Please select current clinical status');
      return false;
    }

    if (!response_to_treatment) {
      toast.error('Please select response to treatment');
      return false;
    }

    if (!session_narrative || session_narrative.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
      setShowNarrativeError(true);
      toast.error(
        `Session narrative should have minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`,
      );
      return false;
    }

    if (
      !overall_progress_toward_mastery ||
      overall_progress_toward_mastery.length < MINIMUM_REQUIRED_SENTENCE_LENGTH
    ) {
      toast.error(
        `Overall progress toward mastery should have minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`,
      );
      return false;
    }

    return true;
  };

  const handleConclude = () => {
    if (!validate()) {
      return;
    }

    setOpenSignature(true);
  };

  const handleSign = async (dataUrl: string) => {
    if (!updatedSupervision) return;

    setSigning(true);

    const endDate = moment(new Date());
    const res = await dispatch(
      clientSupervisionActions.stopSupervision({
        supervisionId: updatedSupervision.id,
        data: {
          signature: dataUrl,
          date_signed: endDate,
          utc_offset_date_signed: getUtcOffset(endDate),
        },
        getAccessTokenSilently,
      }),
    );

    setSigning(false);
    if (res.error) {
      return;
    }

    setOpenSignature(false);
    history.navigate('/dashboard');
  };
  const renderBCBASignatureMessage = () => (
    <Box>
      <Typography variant="body1" align="left" color={FORTA_BLACK} fontSize={12}>
        By signing and submitting these notes, plan and/or records (each a “record”), I hereby
        attest that (i) the entry for the date of service listed in the record accurately and
        completely reflects the services rendered by the named clinician herein; (ii) I have
        thoroughly reviewed the records; and (iii) I have discussed all applicable findings,
        outcomes, issues or discrepancies with the client and/or client&apos;s legal guardian, as
        appropriate.
      </Typography>
      <br />
      <Typography variant="body1" align="left" color={FORTA_BLACK} fontSize={12}>
        I understand that any falsification, omission, or concealment of facts may subject me to
        administrative, civil, or criminal liability as well as termination of my employment with
        the company.
      </Typography>
    </Box>
  );
  return (
    <PageContainer sx={{ px: 3, pt: 3 }} dataTestId="clientSupervisionNotePageContainer">
      {(saving || clientSupervisionLoading) && <FProgressBar />}
      <ClientSupervisionInfo
        clientSupervision={updatedSupervision}
        onUpdate={handleUpdate}
        bayActiveAtDateTime={bayActiveAtDateTime}
        isBay6={isBay6}
        cohortAssignmentsLoading={cohortAssignmentsLoading}
      />
      <FBoxContainer
        sx={{ my: 3, height: 'auto', flexGrow: 0, borderRadius: '4px', boxShadow: 'none' }}
        contentStyle={{ p: 2 }}
      >
        <TherapySession
          clientSupervision={updatedSupervision}
          onUpdate={handleUpdate}
          onFieldCheck={(checkedFields) => setCheckedTherapyFields(checkedFields)}
        />
      </FBoxContainer>
      <FBoxContainer
        sx={{ mb: 3, height: 'auto', flexGrow: 0, borderRadius: '4px', boxShadow: 'none' }}
        data-testid="targetPhase"
        title="Define target phase change line:"
        titleVariant="h4"
        headerStyle={{
          borderRadius: '4px 4px 0 0',
        }}
      >
        <DefineTargetPhaseChangeLine clientSupervision={updatedSupervision} />
      </FBoxContainer>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <FBoxContainer
            sx={{ mb: 3, height: 'auto', flexGrow: 0, borderRadius: '4px', boxShadow: 'none' }}
            title="Current clinical status:"
            titleVariant="h4"
            headerStyle={{
              borderRadius: '4px 4px 0 0',
            }}
          >
            <CurrentClinicalStatus clientSupervision={updatedSupervision} onUpdate={handleUpdate} />
          </FBoxContainer>
        </Grid>
        <Grid item xs={6}>
          <FBoxContainer
            sx={{ mb: 3, height: 'auto', flexGrow: 0, borderRadius: '4px', boxShadow: 'none' }}
            title="Response to treatment:"
            titleVariant="h4"
            headerStyle={{
              borderRadius: '4px 4px 0 0',
            }}
          >
            <ResponseToTreatment clientSupervision={updatedSupervision} onUpdate={handleUpdate} />
          </FBoxContainer>
        </Grid>
      </Grid>
      <FBoxContainer
        sx={{ mb: 3, height: 'auto', flexGrow: 0, borderRadius: '4px', boxShadow: 'none' }}
        title="Session narrative: (Include at least two points of BT feedback)"
        titleVariant="h4"
        headerStyle={{
          borderRadius: '4px 4px 0 0',
        }}
      >
        <SessionNarrative
          clientSupervision={updatedSupervision}
          showError={showNarrativeError}
          onUpdate={handleUpdate}
        />
      </FBoxContainer>
      <FBoxContainer
        sx={{ mb: 3, height: 'auto', flexGrow: 0, borderRadius: '4px', boxShadow: 'none' }}
        title="Overall Progress Toward Mastery:"
        titleVariant="h4"
        headerStyle={{
          borderRadius: '4px 4px 0 0',
        }}
      >
        <OverallProgressTowardMastery
          clientSupervision={updatedSupervision}
          onUpdate={handleUpdate}
        />
      </FBoxContainer>
      {isEditable && (
        <Box sx={{ display: 'flex', justifyContent: 'end' }}>
          <FHButton
            data-testid="concludeclientSupervisionButton"
            sx={{ minWidth: 190 }}
            onClick={handleConclude}
          >
            Conclude
          </FHButton>
        </Box>
      )}

      <FHSignatureDialog
        headerText="Signature"
        cancelText="Clear Signature"
        open={openSignature}
        signing={signing}
        onClose={() => setOpenSignature(false)}
        onSign={(dataURL: string) => handleSign(dataURL)}
        contentRenderer={renderBCBASignatureMessage}
      />
    </PageContainer>
  );
}

export default ClientSupervisionNote;
